var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, Stack, TextField, Typography, } from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useRefresh } from "react-admin";
import { downloadBlob } from "@sequentech/ui-core";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { IPermissions } from "@/types/keycloak";
import { GET_READINESS_EVENTS, READINESS_CHECK } from "@/queries/ReadinessCheck";
import { readinessReport, READINESS_CHECK_KINDS, } from "@/services/readinessReport";
import { findingText } from "@/services/readinessFindings";
/** The server's own message for a check that could not be completed. */
var serverMessage = function (errors) {
    if (!Array.isArray(errors))
        return null;
    var messages = errors
        .map(function (error) { return (error && typeof error.message === "string" ? error.message : ""); })
        .filter(function (message) { return message.trim(); });
    return messages.length ? messages.join("; ") : null;
};
var knownKind = function (kind) { return READINESS_CHECK_KINDS.some(function (known) { return known === kind; }); };
export function ReadinessDialog(_a) {
    var _this = this;
    var _b;
    var event = _a.event, tenantId = _a.tenantId, onClose = _a.onClose;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var alias = useAliasRenderer();
    var refresh = useRefresh();
    var _d = useState(null), tested = _d[0], setTested = _d[1];
    var _e = useState(null), report = _e[0], setReport = _e[1];
    var _f = useState(false), pending = _f[0], setPending = _f[1];
    var _g = useState(null), failure = _g[0], setFailure = _g[1];
    var _h = useState(null), failureDetail = _h[0], setFailureDetail = _h[1];
    var _j = useQuery(GET_READINESS_EVENTS, {
        variables: { tenantId: tenantId, officialEventId: event.id },
        context: { headers: { "x-hasura-role": IPermissions.ELECTION_EVENT_READ } },
        fetchPolicy: "network-only",
    }), data = _j.data, loading = _j.loading, error = _j.error, refetch = _j.refetch;
    var check = useMutation(READINESS_CHECK)[0];
    var eventLabel = function (option) {
        return "".concat(alias(option), " \u00B7 ").concat(option.id).concat(option.is_archived ? " \u00B7 ".concat(t("readiness.archived")) : "");
    };
    var collator = new Intl.Collator(i18n.language, { numeric: true, sensitivity: "base" });
    var options = ((_b = data === null || data === void 0 ? void 0 : data.sequent_backend_election_event) !== null && _b !== void 0 ? _b : [])
        .filter(function (option) { return option.id !== event.id; })
        .sort(function (left, right) {
        return collator.compare(alias(left), alias(right)) ||
            String(left.id).localeCompare(String(right.id));
    });
    var run = function () { return __awaiter(_this, void 0, void 0, function () {
        var response, result, error_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!tested || tested.id === event.id || pending || loading || error)
                        return [2 /*return*/];
                    setPending(true);
                    setReport(null);
                    setFailure(null);
                    setFailureDetail(null);
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, check({
                            variables: { official_event_id: event.id, tested_event_id: tested.id },
                            context: { headers: { "x-hasura-role": IPermissions.READINESS_CHECK } },
                        })];
                case 2:
                    response = _c.sent();
                    result = readinessReport((_a = response.data) === null || _a === void 0 ? void 0 : _a.readiness_check, event.id, tested.id);
                    if (((_b = response.errors) === null || _b === void 0 ? void 0 : _b.length) || !result) {
                        setFailure(t("readiness.error"));
                        setFailureDetail(serverMessage(response.errors));
                        return [2 /*return*/];
                    }
                    setReport(result);
                    if (result.passed)
                        refresh();
                    return [3 /*break*/, 5];
                case 3:
                    error_1 = _c.sent();
                    setFailure(t("readiness.error"));
                    setFailureDetail(serverMessage(error_1 === null || error_1 === void 0 ? void 0 : error_1.graphQLErrors));
                    return [3 /*break*/, 5];
                case 4:
                    setPending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var download = function () { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!report)
                        return [2 /*return*/];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, downloadBlob(new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }), "readiness-".concat(event.id, ".json"))];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    setFailure(t("readiness.downloadError"));
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var retryEvents = function () { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, refetch()];
                case 1:
                    _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Dialog, { open: true, fullWidth: true, maxWidth: "md", onClose: pending ? undefined : onClose, "aria-labelledby": "readiness-title", children: [_jsx(DialogTitle, { id: "readiness-title", children: t("readiness.title") }), _jsx(DialogContent, { dividers: true, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Typography, { children: t("readiness.description") }), _jsxs(Box, { sx: { overflowWrap: "anywhere" }, children: [_jsx(Typography, { variant: "subtitle2", component: "p", children: t("logsScreen.readiness.fields.official") }), _jsx(Typography, { children: alias(event) }), _jsx(Typography, { variant: "body2", children: event.id })] }), _jsx(Autocomplete, { options: options, value: tested, loading: loading, disabled: pending || loading || !!error, isOptionEqualToValue: function (left, right) { return left.id === right.id; }, getOptionLabel: eventLabel, onChange: function (_event, value) {
                                setTested(value);
                                setReport(null);
                                setFailure(null);
                                setFailureDetail(null);
                            }, noOptionsText: t("readiness.noEvents"), loadingText: t("common.label.loadingData"), renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: t("readiness.testedEvent") }))); }, renderOption: function (props, option) { return (_createElement("li", __assign({}, props, { key: option.id }),
                                _jsx(Typography, { sx: { overflowWrap: "anywhere" }, children: eventLabel(option) }))); } }), error && (_jsx(Alert, { severity: "error", action: _jsx(Button, { onClick: retryEvents, children: t("readiness.retry") }), children: t("readiness.eventsError") })), failure && (_jsxs(Alert, { severity: "error", children: [_jsx(Typography, { children: failure }), failureDetail && (_jsx(Typography, { variant: "body2", sx: { overflowWrap: "anywhere" }, children: failureDetail }))] })), pending && (_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", role: "status", children: [_jsx(CircularProgress, { size: 20, "aria-label": t("readiness.running") }), _jsx(Typography, { children: t("readiness.running") })] })), report && (_jsxs(_Fragment, { children: [_jsx(Alert, { severity: report.passed ? "success" : "warning", children: t(report.passed ? "readiness.success" : "readiness.failed") }), _jsxs(Typography, { children: [t("logsScreen.readiness.fields.checked_at"), ": ", report.checked_at] }), _jsx(List, { disablePadding: true, "aria-label": t("readiness.results"), children: report.checks.map(function (check) {
                                        var _a;
                                        return (_jsxs(ListItem, { disableGutters: true, sx: { gap: 1, alignItems: "flex-start" }, children: [_jsx(Chip, { size: "small", sx: { flexShrink: 0 }, color: check.passed ? "success" : "error", label: t(check.passed
                                                        ? "logsScreen.readiness.passed"
                                                        : "logsScreen.readiness.failed") }), _jsxs(Box, { sx: { minWidth: 0, overflowWrap: "anywhere" }, children: [_jsx(Typography, { sx: { m: 0 }, children: knownKind(check.kind)
                                                                ? t("logsScreen.readiness.checks.".concat(check.kind))
                                                                : check.message }), knownKind(check.kind) && (_jsxs(Typography, { variant: "body2", color: "text.secondary", sx: { m: 0 }, children: [t("readiness.condition"), ":", " ", _jsx("span", { children: check.message })] })), !check.passed &&
                                                            (((_a = check.findings) === null || _a === void 0 ? void 0 : _a.length) ? (_jsx(Box, { component: "ul", "aria-label": t("readiness.reasons"), sx: { m: 0, pl: 2 }, children: check.findings.map(function (finding, index) { return (_jsx(Typography, { component: "li", variant: "body2", children: findingText(t, finding) }, index)); }) })) : (_jsx(Typography, { variant: "body2", sx: { m: 0 }, children: t("readiness.noDetail") })))] })] }, check.kind));
                                    }) }), report.log_entry_id && (_jsxs(Box, { sx: { overflowWrap: "anywhere" }, children: [_jsx(Typography, { variant: "subtitle2", component: "p", children: t("logsScreen.readiness.fields.log_entry_id") }), _jsx(Typography, { children: report.log_entry_id })] })), _jsx(Typography, { variant: "body2", children: t("readiness.snapshot") })] }))] }) }), _jsxs(DialogActions, { sx: { flexWrap: "wrap", gap: 1 }, children: [_jsx(Button, { disabled: pending, onClick: onClose, children: t("readiness.close") }), report && _jsx(Button, { onClick: download, children: t("readiness.download") }), _jsx(Button, { variant: "contained", disabled: !tested || pending || loading || !!error, onClick: run, children: t(pending ? "readiness.running" : "readiness.run") })] })] }));
}
/** The server checks both events' access and signs only a pass. */
export function ReadinessCheckButton(_a) {
    var event = _a.event;
    var auth = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var t = useTranslation().t;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    if (!tenantId || !auth.isAuthorized(true, tenantId, IPermissions.READINESS_CHECK))
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "text", size: "small", startIcon: _jsx(FactCheckOutlinedIcon, {}), onClick: function () { return setOpen(true); }, children: t("readiness.action") }), open && (_jsx(ReadinessDialog, { event: event, tenantId: tenantId, onClose: function () { return setOpen(false); } }, "".concat(tenantId, ":").concat(event.id)))] }));
}
